/*----------------------------------
- PRODUCT SESSION, READ ONCE PER DOCUMENT
----------------------------------*/

/**
 * @docs docs/features/website-landing-experience
 * @fix  docs/fixes/2026-09-14-website-header-blind-to-members.md
 * @rule The Website learns who the visitor is in the browser, once per document, and
 *       never from SSR. A failed read is terminal and resolves `null`.
 *
 * The Website cannot know the member server-side. Its public pages carry
 * `options.static`, so one render is replayed verbatim to everyone, and the Product
 * session cookie is host-only on the Product host, so `unique.domains` never receives
 * it (see apps/website/server/lib/productConnectionCookies.ts). Identity is therefore a
 * browser-side fact, and this module is the one place that establishes it.
 *
 * One probe per document, shared by every consumer, is what makes that affordable: the
 * client request-discipline contract forbids accumulating one mount-time call per
 * component, and the header, the metrics component and the `/domains` GitHub intent all
 * want the same answer. There is no refresh: an authentication inside the document ends
 * with the conversion modal handing the browser to the Product, which is a new document.
 */

import type { CurrentUserSession } from '@uniquedomains/components/contracts/users/session';
// Relative, like the other client services that this module's unit test imports directly:
// the shared vitest alias maps `@` to the Product app, so `@/common/...` resolves there.
import { appErrorReporter } from '../../../../common/libs/errorReporting';
import { readDirectProductCurrentUser } from './directProductRequest';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/**
 * Passed as the fallback message so the Website error handler logs to the console and
 * shows nothing: a visitor who is simply not signed in must never see a toast, and the
 * bug reporter is wired to `window.onerror`, which this never reaches.
 */
export const PRODUCT_SESSION_UNAVAILABLE_MESSAGE = 'The product session could not be read.';

/*----------------------------------
- STATE
----------------------------------*/

export type ProductSessionSnapshot = {
    session: CurrentUserSession | null;
    /** Whether this document has finished its probe, successfully or not. */
    settled: boolean;
};

type ProductSessionCache = ProductSessionSnapshot & {
    promise: Promise<CurrentUserSession | null> | null;
};

const cache: ProductSessionCache = { promise: null, session: null, settled: false };

/*----------------------------------
- HELPERS
----------------------------------*/

const probeProductSession = async (productOrigin: string): Promise<CurrentUserSession | null> => {
    try {
        return await readDirectProductCurrentUser(productOrigin);
    } catch (error) {
        /*
         * Every failure is the same answer here: this document does not get to know the
         * member. Timeout, offline, HTTP error, unparseable body, all of them mean the
         * page renders as a guest, and none of them is worth a second attempt: the
         * client contract treats a terminal error as ineligible for automatic
         * re-request, and a retry storm from a shared header would hit every page view.
         */
        appErrorReporter.app.handleError(error, PRODUCT_SESSION_UNAVAILABLE_MESSAGE);
        return null;
    }
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

/** What this document already knows, without starting a read. */
export const readCachedProductSession = (): ProductSessionSnapshot => ({
    session: cache.session,
    settled: cache.settled,
});

/**
 * The Product session for this document. Concurrent callers share one in-flight promise,
 * and every later caller is answered from the cache without a request.
 */
export const readProductSession = (productOrigin: string): Promise<CurrentUserSession | null> => {
    // SSR renders the guest markup and must not fetch: the output is cached and shared.
    if (typeof window === 'undefined') return Promise.resolve(null);
    if (cache.settled) return Promise.resolve(cache.session);

    cache.promise ??= probeProductSession(productOrigin).then((session) => {
        cache.session = session;
        cache.settled = true;
        cache.promise = null;
        return session;
    });

    return cache.promise;
};

/** Test seam: drops the per-document cache so each case starts from a cold page. */
export const resetProductSessionCacheForTests = () => {
    cache.promise = null;
    cache.session = null;
    cache.settled = false;
};
